import { useState } from 'react';

import Icon from './Icon.jsx';

/**
 * Saved + Review, sharing one screen because they're the same interaction on
 * two different filters: a list of verses you can expand, study as a deck, or
 * hand to Scripture AI.
 *
 *   bookmarks — verses you explicitly saved
 *   review    — verses you marked "still learning"
 */
export default function VerseListScreen({
  bookmarkedVerses,
  reviewVerses,
  bookmarks,
  learning,
  initialMode = 'bookmarks',
  onToggleBookmark,
  onMark,
  onAsk,
  onStartDeck,
  onNavigate,
}) {
  const [mode, setMode] = useState(initialMode);
  const verses = mode === 'bookmarks' ? bookmarkedVerses : reviewVerses;

  return (
    <section className="screen">
      <div className="screen__heading">
        <h2>{mode === 'bookmarks' ? 'Saved verses' : 'Review'}</h2>
        <p>
          {mode === 'bookmarks'
            ? 'Everything you’ve bookmarked, in one place.'
            : 'Verses you marked “still learning”. Come back until they stick.'}
        </p>
      </div>

      <div className="segmented" role="tablist" aria-label="List mode">
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'bookmarks'}
          className={`segmented__btn${mode === 'bookmarks' ? ' is-active' : ''}`}
          onClick={() => setMode('bookmarks')}
        >
          <Icon name="ribbon" size={15} />
          Saved ({bookmarkedVerses.length})
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'review'}
          className={`segmented__btn${mode === 'review' ? ' is-active' : ''}`}
          onClick={() => setMode('review')}
        >
          <Icon name="renew" size={15} />
          Review ({reviewVerses.length})
        </button>
      </div>

      {verses.length === 0 ? (
        <div className="empty">
          <p className="empty__icon">
            <Icon name={mode === 'bookmarks' ? 'ribbon' : 'renew'} size={44} />
          </p>
          <h3>{mode === 'bookmarks' ? 'No saved verses yet' : 'Nothing to review'}</h3>
          <p>
            {mode === 'bookmarks'
              ? 'Tap Save on any card and it’ll show up here.'
              : 'Mark a verse “still learning” while studying and it lands here.'}
          </p>
          <button type="button" className="btn btn--primary" onClick={() => onNavigate('flashcards')}>
            Study verses
          </button>
        </div>
      ) : (
        <>
          <button
            type="button"
            className="btn btn--primary btn--block"
            onClick={() => onStartDeck({ type: mode })}
          >
            Study these {verses.length} as a deck
          </button>

          <ul className="verse-list">
            {verses.map((verse) => (
              <VerseRow
                key={verse.id}
                verse={verse}
                saved={bookmarks.includes(verse.id)}
                status={learning[verse.id]}
                onToggleBookmark={onToggleBookmark}
                onMark={onMark}
                onAsk={onAsk}
              />
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

function VerseRow({ verse, saved, status, onToggleBookmark, onMark, onAsk }) {
  const [open, setOpen] = useState(false);

  return (
    <li className={`verse-row${open ? ' is-open' : ''}`}>
      <button
        type="button"
        className="verse-row__head"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="verse-row__ref">{verse.ref}</span>
        <span className="verse-row__meta">
          {status ? (
            <span className={`status-chip status-chip--${status}`}>
              {status === 'known' ? 'Known' : 'Learning'}
            </span>
          ) : null}
          <span className="verse-row__chev" aria-hidden="true">
            {open ? '−' : '+'}
          </span>
        </span>
      </button>

      {open ? (
        <div className="verse-row__body">
          <p className="verse-row__text">{verse.text}</p>
          <div className="verse-row__actions">
            <button
              type="button"
              className={`icon-btn icon-btn--sm${saved ? ' is-on' : ''}`}
              onClick={() => onToggleBookmark(verse.id)}
            >
              <Icon name="ribbon" size={14} />
              {saved ? 'Saved' : 'Save'}
            </button>
            <button
              type="button"
              className="icon-btn icon-btn--sm"
              onClick={() => onMark(verse.id, status === 'known' ? 'learning' : 'known')}
            >
              <Icon name={status === 'known' ? 'renew' : 'check'} size={14} />
              {status === 'known' ? 'Move to review' : 'Mark known'}
            </button>
            <button type="button" className="icon-btn icon-btn--sm" onClick={() => onAsk(verse)}>
              <Icon name="dove" size={14} />
              Ask
            </button>
          </div>
        </div>
      ) : null}
    </li>
  );
}
